import { USER_DB_REF } from "../constants/firestore";
import { DEFAULT_CAREGIVER_TABLE_FILTER } from "../constants/flters";
import { DEFAULT_TABLE_PAGINATION } from "../constants/pagination";
import { getNumberOfTraitsMetadata, getPositiviePersonalitiesMetadata, getStringKeywords, ts2Age } from "../helpers/utils";
import { CareGiver, CareGiverFormData } from "../types/caregivers";
import { TablePaginationConfig, TablePaginationResponse } from "../types/common";
import { CaregiverTableFilter } from "../types/filters";
import { FirestoreQueryConstraint } from "../types/firebase";
import { firestoreBatch, firestoreBuildQuery, firestoreDocDelete, firestoreDocRead, firestoreDocRef, firestoreDocUpdate, firestoreElasticSearch, firestoreEndBefore, firestoreExecuteQuery, firestoreLimit, firestoreLimitToLast, firestoreNewDocRef, firestoreOrderBy, firestoreStartAfter, firestoreWhere, firestoreWrite } from "./common/firebase";
import { doesFamilyExists } from "./families";
import { getMaxPageNumber, getNextTablePage } from "./tablesPagination";

export async function initCaregiverTable(
  uid: string
): Promise<TablePaginationResponse<CareGiver>> {
  const { maxPage, size } = DEFAULT_TABLE_PAGINATION;
  const firstPage: TablePaginationConfig = { page: 1, size, maxPage };
  const page = await loadCaregiverNextTablePage(uid, firstPage, DEFAULT_CAREGIVER_TABLE_FILTER);
  return page;
}

export async function loadCaregiverNextTablePage(
  uid: string,
  paginationConfig: TablePaginationConfig,
  filter: CaregiverTableFilter
): Promise<TablePaginationResponse<CareGiver>> {
  const res: CareGiver[] = [];
  const { size, lastDoc } = paginationConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  // filtri
  const { name, surname, age, engaged, notEngaged, coverage } = filter;
  let name_surname_filter = name ? name.toLocaleLowerCase() : '';
  if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  if (name_surname_filter.length > 0) {
    queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter))
  }
  if (age) {
    const ageFilter = parseInt(age);
    if (Number.isInteger(ageFilter)) queryConstraints.push(firestoreWhere('metadata.age', '==', ageFilter));
  }
  if (notEngaged !== engaged) { // quando hanno entrambi lo stesso valore non hanno effetto sui filtri
    queryConstraints.push(firestoreWhere('assignedTo', notEngaged ? '==' : '!=', null));
  }
  if (coverage) {
    queryConstraints.push(firestoreWhere('personalData.coverageAvailability', '==', coverage));
  }
  // paginazione
  queryConstraints.push(firestoreOrderBy('tsCreated', 'desc'));
  if (lastDoc) queryConstraints.push(firestoreStartAfter(lastDoc));
  queryConstraints.push(firestoreLimit(size));
  // esecuzione
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function loadCaregiverPreviousTablePage(
  uid: string,
  paginationConfig: TablePaginationConfig,
  filter: CaregiverTableFilter
): Promise<TablePaginationResponse<CareGiver>> {
  const res: CareGiver[] = [];
  const { size, firstDoc } = paginationConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  // filters 
  const { name, surname, age, engaged, notEngaged, coverage } = filter;
  let name_surname_filter = name ? name.toLowerCase() : '';
  if (surname) name_surname_filter += `/${surname.toLowerCase()}`;
  if (name_surname_filter.length > 0) {
    queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter));
  }
  if (age) {
    const ageFilter = parseInt(age);
    if (Number.isInteger(ageFilter)) queryConstraints.push(firestoreWhere('metadata.age', '==', ageFilter));
  }
  if (notEngaged !== engaged) { // quando hanno entrambi lo stesso valore non hanno effetto sui filtri
    queryConstraints.push(firestoreWhere('assignedTo', notEngaged ? '==' : '!=', null));
  }
  if (coverage) {
    queryConstraints.push(firestoreWhere('personalData.coverageAvailability', '==', coverage));
  }
  // paginazione
  queryConstraints.push(firestoreOrderBy('tsCreated', 'desc'));
  queryConstraints.push(firestoreEndBefore(firstDoc));
  queryConstraints.push(firestoreLimitToLast(size));
  // esecuzione
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function createUserCareGiver(
  uid: string,
  caregiverFormData: CareGiverFormData,
): Promise<CareGiver> {
  const caregiverDocRef = firestoreNewDocRef(`${USER_DB_REF}/${uid}/caregivers`);
  const { name, surname } = caregiverFormData.personalData;
  const nameSurnameKeyword = getStringKeywords(name, surname);
  const ageMetadata = ts2Age(caregiverFormData.personalData.tsDateOfBirth);
  const positivePersonalities = getPositiviePersonalitiesMetadata(caregiverFormData.personalities);
  const numberOfTraitsMetadata = getNumberOfTraitsMetadata(caregiverFormData.traits);
  const caregiverData: CareGiver = { 
    ...caregiverFormData, 
    id: caregiverDocRef.id, 
    tsCreated: Date.now(),
    tsUpdated: Date.now(),
    assignedTo: null,
    metadata: {
      keywords: nameSurnameKeyword,
      normalizedSurname: surname.toLocaleLowerCase(),
      numberOfTraits: numberOfTraitsMetadata,
      positivePersonalities,
      age: ageMetadata,
    }
  };
  await firestoreWrite(`${USER_DB_REF}/${uid}/caregivers`, caregiverDocRef.id, caregiverData);
  return caregiverData;
}

export async function updateUserCaregiver(
  uid: string,
  caregiverData: CareGiver,
): Promise<void> {
  await firestoreWrite(`${USER_DB_REF}/${uid}/caregivers`, caregiverData.id, caregiverData);
}

export async function doesCaregiverExists(
  uid: string,
  caregiverId: string,
): Promise<boolean> {
  const caregiverSnap = await firestoreDocRead(`${USER_DB_REF}/${uid}/caregivers`, caregiverId);
  return caregiverSnap.exists();
}

export async function getUserCaregiver(
  uid: string,
  caregiverId: string,
): Promise<CareGiver> {
  const caregiverSnap = await firestoreDocRead(`${USER_DB_REF}/${uid}/caregivers`, caregiverId);
  return caregiverSnap.data() as CareGiver;
}

export async function getSingleUserCareGiver(
  uid: string,
  id: string
): Promise<CareGiver> {
  const snap = await firestoreDocRead(`${USER_DB_REF}/${uid}/caregivers`, id);
  return snap.data() as CareGiver;
}

export async function updateCaregiverWarning(
  uid: string,
  caregiverId: string,
  warningValue: boolean
): Promise<void> {
  const updateObj = { warning: warningValue };
  await firestoreDocUpdate(`${USER_DB_REF}/${uid}/caregivers`, caregiverId, updateObj);
}

export async function deleteUserCareGiver(
  uid: string,
  caregiver: CareGiver
): Promise<void> {
  if (caregiver.assignedTo) {
    const familyExixts = await doesFamilyExists(uid, caregiver.assignedTo);
    if (familyExixts) {
      const caregiverRef = firestoreDocRef(`${USER_DB_REF}/${uid}/caregivers`, caregiver.id);
      const familyRef = firestoreDocRef(`${USER_DB_REF}/${uid}/families`, caregiver.assignedTo);
      const batch = firestoreBatch();
      const updateObj = { assignedCaregiver: null };
      batch.update(familyRef, updateObj);
      batch.delete(caregiverRef);
      await batch.commit();
    } else {
      await firestoreDocDelete(`${USER_DB_REF}/${uid}/caregivers`, caregiver.id);
    }
  } else {
    await firestoreDocDelete(`${USER_DB_REF}/${uid}/caregivers`, caregiver.id);
  }
}

export async function loadCaregiversFirstTablePage(
  uid: string,
): Promise<TablePaginationResponse<CareGiver>> {
  const { maxPage, page: currentPage, size } = DEFAULT_TABLE_PAGINATION;
  const firstPageConfig: TablePaginationConfig = { page: 1, size, maxPage };
  const page = await getNextTablePage<CareGiver>(`${USER_DB_REF}/${uid}/caregivers`, firstPageConfig);
  return page;
} 

export async function getCaregiversTableMaxPageNumber(
  uid: string,
  pageSize: number,
  filter?: CaregiverTableFilter
): Promise<number> {
  const queryConstraints: FirestoreQueryConstraint[] = [];
  if (filter) {
    const { name, surname, age, engaged, notEngaged, coverage } = filter;
    let name_surname_filter = name ? name.toLocaleLowerCase() : '';
    if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
    if (name_surname_filter.length > 0) { // perchè contiene sempre aleno il carattere speciale
      queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter))
    }
    if (age) {
      const ageFilter = parseInt(age);
      if (Number.isInteger(ageFilter)) queryConstraints.push(firestoreWhere('metadata.age', '==', ageFilter));
    }
    if (notEngaged !== engaged) { // quando hanno entrambi lo stesso valore non hanno effetto sui filtri
      queryConstraints.push(firestoreWhere('assignedTo', notEngaged ? '==' : '!=', null));
    }
    if (coverage) {
      queryConstraints.push(firestoreWhere('personalData.coverageAvailability', '==', coverage));
    }
  }
  return getMaxPageNumber(`${USER_DB_REF}/${uid}/caregivers`, pageSize, queryConstraints.length > 0 ? queryConstraints : undefined);
}

export async function searchCaregiverByName(
  uid: string,
  name: string,
): Promise<CareGiver[]> {
  const searchRes: CareGiver[] = [];
  const querySnap = await firestoreElasticSearch(`${USER_DB_REF}/${uid}/caregivers`, 'personalData.name', name);
  /* const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('metadata.keywords', 'array-contains', name.toLowerCase()),
    firestoreOrderBy('tsCreated'),
    firestoreLimit(3),
  ];
  console.log(name)
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query); */
  querySnap.forEach(el => searchRes.push(el.data() as CareGiver));
  console.log('qui', searchRes);
  return searchRes;
}

export async function initCaregiverSearchResults(
  uid: string
): Promise<CareGiver[]> {
  const res: CareGiver[] = [];
  const queryConstraints: FirestoreQueryConstraint[] = [firestoreOrderBy('tsUpdated', 'desc'), firestoreLimit(5)];
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return res;
}

export function getTsAgeRange({ 
  minAge, maxAge
}: {minAge: number; maxAge: number}): {
  start: number;
  end: number
} {
  const now = new Date();
  const startYear = now.getFullYear() - maxAge;
  const endYear = now.getFullYear() - minAge;
  console.log({startYear, endYear});
  const minDate = new Date(startYear, now.getMonth(), now.getDate()); 
  const maxDate = new Date(endYear, now.getMonth(), now.getDate(), 23, 59, 59, 999); 
  return {
    start: minDate.getTime(),
    end: maxDate.getTime(),
  }
}

export async function searchCaregiverByNameSurname(
  uid: string,
  name: string,
  surname: string,
) {
  const res: CareGiver[] = [];
  let name_surname_filter = name.length > 0 ? name.toLocaleLowerCase() : '';
  if (surname.length > 0) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter),
    firestoreOrderBy('tsUpdated', 'desc')
  ];
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return res;
}

// SUGGESTED CAREGIVERS
export async function initSuggestedCaregiversTable(
  uid: string
): Promise<TablePaginationResponse<CareGiver>> {
  const { maxPage, size } = DEFAULT_TABLE_PAGINATION;
  const firstPage: TablePaginationConfig = { page: 1, size, maxPage };
  const page = loadSuggestedCaregiverNextTablePage(uid, firstPage); 
  return page;
}

export async function loadSuggestedCaregiverNextTablePage(
  uid: string,
  paginationConfig: TablePaginationConfig,
): Promise<TablePaginationResponse<CareGiver>> {
  const res: CareGiver[] = [];
  const { size, lastDoc } = paginationConfig;
  const { end: endTs, start: startTs } = getTsAgeRange({maxAge: 62, minAge: 48});
  const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('personalData.references', '==', true),
    firestoreWhere('yearsOfExperience', '>=', 7), 
    firestoreWhere('languageKnowledge', '>=', 7),
    firestoreWhere('coockingKnowledge', '>=', 7),
    firestoreWhere('personalData.tsDateOfBirth', '<=', endTs),
    firestoreWhere('personalData.tsDateOfBirth', '>=', startTs),
    firestoreWhere('smokes', '==', '1'),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.numberOfTraits', '>=', 3),
    firestoreOrderBy('tsCreated')
  ];
  if (lastDoc) queryConstraints.push(firestoreStartAfter(lastDoc));
  queryConstraints.push(firestoreLimit(size));
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function loadSuggestedCaregiverPrevTablePage(
  uid: string,
  paginationConfig: TablePaginationConfig
): Promise<TablePaginationResponse<CareGiver>> {
  const res: CareGiver[] = [];
  const { size, firstDoc } = paginationConfig;
  const { end: endTs, start: startTs } = getTsAgeRange({maxAge: 62, minAge: 48});
  const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('personalData.references', '==', true),
    firestoreWhere('yearsOfExperience', '>=', 7), 
    firestoreWhere('languageKnowledge', '>=', 7),
    firestoreWhere('coockingKnowledge', '>=', 7),
    firestoreWhere('personalData.tsDateOfBirth', '<=', endTs),
    firestoreWhere('personalData.tsDateOfBirth', '>=', startTs),
    firestoreWhere('smokes', '==', '1'),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.numberOfTraits', '>=', 3),
    firestoreOrderBy('tsCreated'),
    firestoreEndBefore(firstDoc),
    firestoreLimitToLast(size)
  ];
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function getSuggestedCareGiverMaxNumberOfPagses(
  uid: string,
  pageSize: number,
): Promise<number> {
  const { end: endTs, start: startTs } = getTsAgeRange({maxAge: 62, minAge: 48}); 
  const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('personalData.references', '==', true),
    firestoreWhere('yearsOfExperience', '>=', 7), 
    firestoreWhere('languageKnowledge', '>=', 7),
    firestoreWhere('coockingKnowledge', '>=', 7),
    firestoreWhere('personalData.tsDateOfBirth', '<=', endTs),
    firestoreWhere('personalData.tsDateOfBirth', '>=', startTs),
    firestoreWhere('smokes', '==', '1'),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.numberOfTraits', '>=', 3),
  ];
  return getMaxPageNumber(`${USER_DB_REF}/${uid}/caregivers`, pageSize, queryConstraints);
}

export async function getSuggestedCaregivers(
  uid: string,
): Promise<CareGiver[]> {
  const res: CareGiver[] = [];
  const { end: endTs, start: startTs } = getTsAgeRange({maxAge: 62, minAge: 48}); 
  console.log({endTs, startTs});
  const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('personalData.references', '==', true),
    firestoreWhere('yearsOfExperience', '>=', 7), 
    firestoreWhere('languageKnowledge', '>=', 7),
    firestoreWhere('coockingKnowledge', '>=', 7),
    firestoreWhere('personalData.tsDateOfBirth', '<=', endTs),
    firestoreWhere('personalData.tsDateOfBirth', '>=', startTs),
    firestoreWhere('smokes', '==', '1'),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.positivePersonalities', '>=', 2),
    firestoreWhere('metadata.numberOfTraits', '>=', 3),
    firestoreLimit(7)
  ];
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/caregivers`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as CareGiver));
  return res;
}